/**
 * FilterChip — the selectable pill used across admin list filters
 * (status / kind / role toggles). Replaces the bespoke FilterChip / Chip
 * re-implemented across most list screens. Active state fills with the
 * accent tint; web hover/press feedback. Theme-driven — no hex.
 */
import { Platform, Pressable, Text, type ViewStyle } from "react-native";

import { RADII, SPACING, STROKES } from "../../../lib/brand";
import type { PaneTheme } from "../../brand/paneThemes";

export type FilterChipProps = {
  theme: PaneTheme;
  label: string;
  active: boolean;
  onPress: () => void;
  /** Optional trailing count (e.g. "New · 3"). */
  count?: number;
};

export function FilterChip({
  theme,
  label,
  active,
  onPress,
  count,
}: FilterChipProps) {
  const isWeb = Platform.OS === "web";
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      style={({ hovered, pressed }: { hovered?: boolean; pressed?: boolean }) => [
        {
          flexDirection: "row",
          alignItems: "center",
          gap: 6,
          paddingHorizontal: 12,
          paddingVertical: SPACING.xs,
          borderRadius: RADII.full,
          borderWidth: STROKES.hairline,
          borderColor: active
            ? theme.accentBorderHover
            : hovered || pressed
              ? theme.borderStrong
              : theme.border,
          backgroundColor: active
            ? theme.accentSoft
            : hovered || pressed
              ? theme.neutralHoverBgOnDark
              : "transparent",
        },
        isWeb
          ? ({
              cursor: "pointer",
              transition: "background-color 0.15s ease, border-color 0.15s ease",
            } as unknown as ViewStyle)
          : null,
      ]}
    >
      <Text
        style={{
          fontFamily: active ? theme.bodyBoldFont : theme.bodyMediumFont,
          fontSize: 11,
          letterSpacing: 0.4,
          color: active ? theme.accent : theme.textOnSurfaceDim,
        }}
      >
        {label}
      </Text>
      {count != null && count > 0 ? (
        <Text
          style={{
            fontFamily: theme.bodyBoldFont,
            fontSize: 11,
            color: active ? theme.accent : theme.textOnSurfaceFaint,
          }}
        >
          {count}
        </Text>
      ) : null}
    </Pressable>
  );
}
